import { useTheme } from "@/src/styles/theme-provider";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Platform, View, ViewStyle } from "react-native";

type HoverTooltipProps = Readonly<{
  content: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
  disabled?: boolean;
  style?: ViewStyle;
  className?: string;
  unstyled?: boolean;
}>;

const isWeb = Platform.OS === "web";

export function HoverTooltip({
  content,
  children,
  delay = 500,
  disabled = false,
  style,
  className,
  unstyled = false,
}: HoverTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0, height: 0 });
  const triggerRef = useRef<View>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { varStyle } = useTheme();

  const showTooltip = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      triggerRef.current?.measureInWindow((x, y, _width, height) => {
        setPosition({ x, y, height });
        setVisible(true);
      });
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const tooltipBody = unstyled ? (
    content
  ) : (
    <View className="bg-onyx rounded-md px-2 py-1">{content}</View>
  );

  const tooltipNode =
    visible && isWeb
      ? createPortal(
          <div
            style={{
              position: "fixed",
              top: position.y + position.height + 4,
              left: position.x,
              zIndex: 9999,
              pointerEvents: "none",
              ...(varStyle as React.CSSProperties),
            }}
          >
            {tooltipBody}
          </div>,
          document.body,
        )
      : null;

  return (
    <View
      ref={triggerRef}
      style={style}
      className={className}
      // @ts-expect-error - web-only hover events, valid via react-native-web
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {tooltipNode}
    </View>
  );
}
